import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addEdge,
  Background,
  type Connection,
  Controls,
  type Edge,
  Handle,
  MiniMap,
  type NodeProps,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { type ApiError, api, qs, unwrap } from '../../api/client';
import { useAutomations } from '../../api/hooks';
import type { Flow, FlowDraft, FlowNode, FlowTemplate, Items } from '../../api/types';
import { useSession } from '../../auth/session';
import {
  Badge,
  Card,
  DataTable,
  Empty,
  ErrorAlert,
  Field,
  Modal,
  PageHeader,
  useToast,
} from '../../components/ui';
import {
  applyEdges,
  followerOnlyNode,
  formatOf,
  type GraphNode,
  messageForKind,
  NODE_TYPES,
  newNode,
  outgoingHandles,
  QUESTION_KINDS,
  summarize,
  toGraph,
} from './graph';

function AutodmNode({ data, selected }: NodeProps<GraphNode>) {
  const n = data.node;
  const handles = outgoingHandles(n);
  return (
    <div className={`flow-node ${selected ? 'selected' : ''}`}>
      {n.type !== 'trigger' ? <Handle type="target" position={Position.Top} /> : null}
      <div className="type">{n.type.replace('_', ' ')}</div>
      <div className="truncate" style={{ maxWidth: 200 }}>
        {summarize(n)}
      </div>
      {handles.map((h, i) => (
        <Handle
          key={h.handle}
          id={h.handle}
          type="source"
          position={Position.Bottom}
          style={{ left: `${((i + 1) * 100) / (handles.length + 1)}%` }}
          title={h.handle}
        />
      ))}
    </div>
  );
}
const nodeTypes = { autodm: AutodmNode };

export function NodeEditor({
  node,
  onChange,
  onDelete,
  flows,
}: {
  node: FlowNode;
  onChange: (n: FlowNode) => void;
  onDelete: () => void;
  flows: Flow[];
}) {
  const text = (label: string, value: string, set: (v: string) => void) => (
    <Field label={label}>
      <input className="input" value={value} onChange={(e) => set(e.target.value)} />
    </Field>
  );
  return (
    <Card title={`Edit ${node.type.replace('_', ' ')}`}>
      {node.type === 'trigger' ? (
        <Field label="Starts on">
          <select
            className="input"
            value={node.on}
            onChange={(e) =>
              onChange({
                ...node,
                on: e.target.value as FlowNode & { type: 'trigger' } extends { on: infer O }
                  ? O
                  : never,
              })
            }
          >
            <option value="comment">Comment</option>
            <option value="story_reply">Story reply</option>
            <option value="dm">DM reply</option>
            <option value="story_mention">Story mention</option>
            <option value="post_share">Post shared to DM</option>
          </select>
        </Field>
      ) : null}
      {node.type === 'send_message' ? (
        <>
          <Field label="Format">
            <select
              className="input"
              value={formatOf(node.message)}
              onChange={(e) => {
                const k = e.target.value;
                // UI-35: choosing the format already shown keeps the text and links
                if (k !== formatOf(node.message)) onChange({ ...node, message: messageForKind(k) });
              }}
            >
              <option value="text">Text</option>
              <option value="button">Button</option>
              <option value="image">Image</option>
              <option value="video">Video</option>
              <option value="file">PDF</option>
            </select>
          </Field>
          {node.message.kind === 'text' ? (
            <>
              {text('Message', node.message.text, (v) =>
                onChange({ ...node, message: { ...node.message, kind: 'text', text: v } }),
              )}
              {/* UI-58: unticked writes track:false so the links in the text go out as written */}
              <label className="row" style={{ alignItems: 'center', gap: 6 }}>
                <input
                  type="checkbox"
                  checked={node.message.track !== false}
                  onChange={(e) => {
                    const { track: _t, ...rest } = node.message as {
                      kind: 'text';
                      text: string;
                      track?: false;
                    };
                    onChange({
                      ...node,
                      message: e.target.checked ? rest : { ...rest, track: false as const },
                    });
                  }}
                />
                <span className="small">Track clicks in this text</span>
              </label>
              <span className="help">
                {node.message.track === false
                  ? 'Links sent as written'
                  : 'Links tracked automatically'}
              </span>
            </>
          ) : null}
          {node.message.kind === 'button' ? (
            <>
              {text('Message', node.message.text, (v) =>
                onChange({ ...node, message: { ...node.message, text: v } as never }),
              )}
              {node.message.buttons.map((b, i) => (
                <div className="row" key={`b-${i}`}>
                  {text(`Button ${i + 1} title`, b.title, (v) =>
                    onChange({
                      ...node,
                      message: {
                        ...node.message,
                        buttons: (node.message as { buttons: (typeof b)[] }).buttons.map((x, j) =>
                          j === i ? { ...x, title: v } : x,
                        ),
                      } as never,
                    }),
                  )}
                  {text(`Button ${i + 1} link`, b.url, (v) =>
                    onChange({
                      ...node,
                      message: {
                        ...node.message,
                        buttons: (node.message as { buttons: (typeof b)[] }).buttons.map((x, j) =>
                          j === i ? { ...x, url: v } : x,
                        ),
                      } as never,
                    }),
                  )}
                  <label className="row" style={{ alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={b.track !== false}
                      onChange={(e) =>
                        onChange({
                          ...node,
                          message: {
                            ...node.message,
                            buttons: (node.message as { buttons: (typeof b)[] }).buttons.map(
                              (x, j) => {
                                if (j !== i) return x;
                                const { track: _t, ...rest } = x;
                                return e.target.checked ? rest : { ...rest, track: false as const };
                              },
                            ),
                          } as never,
                        })
                      }
                    />
                    <span className="small">Track clicks</span>
                  </label>
                </div>
              ))}
              {node.message.buttons.length < 3 ? (
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() =>
                    onChange({
                      ...node,
                      message: {
                        ...node.message,
                        buttons: [
                          ...(node.message as { buttons: unknown[] }).buttons,
                          { title: '', url: 'https://' },
                        ],
                      } as never,
                    })
                  }
                >
                  Add button
                </button>
              ) : null}
            </>
          ) : null}
          {node.message.kind === 'image' || node.message.kind === 'video'
            ? text(
                node.message.kind === 'video' ? 'Video URL' : 'Image URL',
                node.message.url,
                (v) =>
                  onChange({
                    ...node,
                    message: { kind: (node.message as { kind: 'image' | 'video' }).kind, url: v },
                  }),
              )
            : null}
          {/* AUT-20 */}
          {node.message.kind === 'file'
            ? text('PDF URL', node.message.url, (v) =>
                onChange({ ...node, message: { kind: 'file', url: v } }),
              )
            : null}
        </>
      ) : null}
      {node.type === 'ai_reply' ? (
        <>
          {text('Instruction', node.instruction, (v) => onChange({ ...node, instruction: v }))}
          {text('Fallback text', node.fallback, (v) => onChange({ ...node, fallback: v }))}
        </>
      ) : null}
      {node.type === 'question' ? (
        <>
          <Field label="Collect">
            <select
              className="input"
              value={node.kind}
              onChange={(e) =>
                onChange({
                  ...node,
                  kind: e.target.value,
                  saveAs: e.target.value === 'text' ? node.saveAs : e.target.value,
                })
              }
            >
              {QUESTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {k.replace('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          {text('Question', node.prompt, (v) => onChange({ ...node, prompt: v }))}
          {text('Retry prompt', node.retryPrompt, (v) => onChange({ ...node, retryPrompt: v }))}
          {text('Save answer as', node.saveAs, (v) => onChange({ ...node, saveAs: v }))}
        </>
      ) : null}
      {node.type === 'condition' ? (
        <>
          {text('Field (answers.<key>, contact.is_follower, contact.username)', node.field, (v) =>
            onChange({ ...node, field: v }),
          )}
          <Field label="Operator">
            <select
              className="input"
              value={node.op}
              onChange={(e) =>
                onChange({ ...node, op: e.target.value as 'contains' | 'equals' | 'exists' })
              }
            >
              <option value="contains">contains</option>
              <option value="equals">equals</option>
              <option value="exists">exists</option>
            </select>
          </Field>
          {node.op !== 'exists'
            ? text('Value', node.value ?? '', (v) => onChange({ ...node, value: v }))
            : null}
          <p className="muted small">
            Connect the <strong>yes</strong> and <strong>no</strong> handles to the next steps.
          </p>
        </>
      ) : null}
      {node.type === 'delay' ? (
        <Field label="Delay (seconds)">
          <input
            className="input"
            type="number"
            min={0}
            value={node.seconds}
            onChange={(e) => onChange({ ...node, seconds: Number(e.target.value) })}
          />
        </Field>
      ) : null}
      {node.type === 'tag' ? text('Tag', node.tag, (v) => onChange({ ...node, tag: v })) : null}
      {node.type === 'untag'
        ? text('Tag to remove', node.tag, (v) => onChange({ ...node, tag: v }))
        : null}
      {node.type === 'randomizer' ? (
        <>
          {node.branches.map((b, i) => (
            <Field key={`b-${i}`} label={`Branch ${i + 1} weight (%)`}>
              <input
                className="input"
                type="number"
                min={0}
                value={b.weight}
                onChange={(e) =>
                  onChange({
                    ...node,
                    branches: node.branches.map((x, j) =>
                      j === i ? { ...x, weight: Number(e.target.value) } : x,
                    ),
                  })
                }
              />
            </Field>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              onChange({ ...node, branches: [...node.branches, { weight: 0, next: null }] })
            }
          >
            Add branch
          </button>
        </>
      ) : null}
      {node.type === 'keyword_router' ? (
        <>
          {node.branches.map((b, i) => (
            <Field key={`k-${i}`} label={`Branch ${i + 1} keywords (comma separated)`}>
              <input
                className="input"
                value={b.keywords.join(', ')}
                onChange={(e) =>
                  onChange({
                    ...node,
                    branches: node.branches.map((x, j) =>
                      j === i
                        ? {
                            ...x,
                            keywords: e.target.value
                              .split(',')
                              .map((k) => k.trim())
                              .filter((k) => k !== ''),
                          }
                        : x,
                    ),
                  })
                }
              />
            </Field>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              onChange({ ...node, branches: [...node.branches, { keywords: [], next: null }] })
            }
          >
            Add branch
          </button>
          <p className="muted small">
            The first branch whose keyword the text contains wins; connect the{' '}
            <strong>otherwise</strong> handle for everything else.
          </p>
        </>
      ) : null}
      {node.type === 'trigger_flow' ? (
        <Field label="Flow to start">
          <select
            className="input"
            value={node.flowId}
            onChange={(e) => onChange({ ...node, flowId: e.target.value })}
          >
            <option value="">Select</option>
            {flows.map((f) => (
              <option key={f.id} value={f.id}>
                {f.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      {node.type === 'handoff'
        ? text('Message to the audience', node.message, (v) => onChange({ ...node, message: v }))
        : null}
      {node.type === 'note' ? text('Note', node.text, (v) => onChange({ ...node, text: v })) : null}
      {node.type !== 'trigger' ? (
        <button type="button" className="btn btn-sm btn-danger" onClick={onDelete}>
          Delete node
        </button>
      ) : null}
    </Card>
  );
}

/** UI-38 (revised 3): a gated editor's canvas is non-interactive, so nothing can dirty a page that cannot save. */
export function canvasProps(gated: boolean): {
  nodesDraggable: boolean;
  nodesConnectable: boolean;
  elementsSelectable: boolean;
  deleteKeyCode: string | null;
} {
  return {
    nodesDraggable: !gated,
    nodesConnectable: !gated,
    elementsSelectable: !gated,
    deleteKeyCode: gated ? null : 'Backspace',
  };
}

/** UI-45 (revised): a pause refused by a parent flow names it in the error details. */
const parentFlowOf = (e: ApiError | null): string | null => {
  const d = e?.details as { flowId?: unknown } | undefined;
  return typeof d?.flowId === 'string' ? d.flowId : null;
};

export function FlowEditorPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useSession();
  const flow = useQuery({
    queryKey: ['flow', id],
    queryFn: () => unwrap(api.get<Flow>(`/portal/flows/${id}`)),
  });
  const flows = useQuery({
    queryKey: ['flows'],
    queryFn: () => unwrap(api.get<Items<Flow>>('/portal/flows')),
  });
  const [defNodes, setDefNodes] = useState<FlowNode[]>([]);
  const [nodes, setNodes, onNodesChange] = useNodesState<GraphNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [name, setName] = useState('');
  // UI-27: the folder is editable here too, not only at creation
  const [folder, setFolder] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (!flow.data) return;
    const g = toGraph(flow.data.definition.nodes);
    setDefNodes(flow.data.definition.nodes);
    setNodes(g.nodes);
    setEdges(g.edges);
    setName(flow.data.name);
    setFolder(flow.data.folder ?? '');
    setDirty(false);
  }, [flow.data, setNodes, setEdges]);
  const definition = useMemo(() => ({ nodes: applyEdges(defNodes, edges) }), [defNodes, edges]);
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.patch<Flow>(`/portal/flows/${id}`, { name, folder: folder.trim() || null, definition }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['flow', id] });
      setDirty(false);
      toast('Flow saved');
    },
  });
  const publish = useMutation({
    mutationFn: async () => {
      await unwrap(
        api.patch(`/portal/flows/${id}`, { name, folder: folder.trim() || null, definition }),
      );
      return unwrap(api.post<Flow>(`/portal/flows/${id}/publish`));
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['flow', id] });
      toast('Flow published');
    },
  });
  const pause = useMutation({
    mutationFn: () => unwrap(api.post<Flow>(`/portal/flows/${id}/pause`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow', id] }),
    // UI-45: a refused pause (flow_in_use) is said, not swallowed
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  // UI-38 (revised) / FLW-14: publishing a template flow needs no flows feature; saving does
  const publishOnly = useMutation({
    mutationFn: () => unwrap(api.post<Flow>(`/portal/flows/${id}/publish`)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['flow', id] });
      toast('Flow published');
    },
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const onConnect = useCallback(
    (c: Connection) => {
      setEdges((es) =>
        addEdge(
          { ...c, id: `${c.source}:${c.sourceHandle ?? 'next'}` },
          es.filter(
            (e) =>
              !(e.source === c.source && (e.sourceHandle ?? 'next') === (c.sourceHandle ?? 'next')),
          ),
        ),
      );
      setDirty(true);
    },
    [setEdges],
  );
  const updateNode = (n: FlowNode) => {
    setDefNodes((ns) => ns.map((x) => (x.id === n.id ? n : x)));
    setNodes((gs) => gs.map((g) => (g.id === n.id ? { ...g, data: { node: n } } : g)));
    setDirty(true);
  };
  // UI-31: a palette entry may carry a preconfigured node (follower only)
  const addPrebuilt = (n: FlowNode) => {
    setDefNodes((ns) => [...ns, n]);
    setNodes((gs) => [
      ...gs,
      {
        id: n.id,
        type: 'autodm',
        position: { x: 120 + gs.length * 30, y: 80 + gs.length * 20 },
        data: { node: n },
      },
    ]);
    setSelected(n.id);
    setDirty(true);
  };
  const addNode = (type: (typeof NODE_TYPES)[number]) => addPrebuilt(newNode(type));
  const deleteNode = (nid: string) => {
    setDefNodes((ns) => ns.filter((x) => x.id !== nid));
    setNodes((gs) => gs.filter((g) => g.id !== nid));
    setEdges((es) => es.filter((e) => e.source !== nid && e.target !== nid));
    setSelected(null);
    setDirty(true);
  };
  const selectedNode = defNodes.find((n) => n.id === selected) ?? null;
  const err = (save.error ?? publish.error ?? pause.error ?? publishOnly.error) as ApiError | null;
  // UI-38: a plan without flows can look but not save; the API refuses too (requireFlowFeature)
  const gated = me?.features.flows === false;
  // UI-41: the PRD's View; members without flow:manage read the editor, inputs disabled.
  // UI-63: `?view=1` opens the same read-only editor on purpose
  const [search] = useSearchParams();
  const viewing = search.get('view') === '1';
  const readOnly = !can('flow:manage') || viewing;
  return (
    <>
      <PageHeader
        title={flow.data?.name ?? 'Flow'}
        subtitle={
          flow.data ? `Status: ${flow.data.status}${dirty ? ' · unsaved changes' : ''}` : ''
        }
        actions={
          <>
            <Link className="btn" to="/app/flows">
              Back
            </Link>
            {viewing && can('flow:manage') ? (
              <Link className="btn btn-primary" to={`/app/flows/${id}`}>
                Edit
              </Link>
            ) : null}
            {can('flow:manage') && !viewing ? (
              <button
                type="button"
                className="btn"
                disabled={save.isPending || gated}
                onClick={() => save.mutate()}
              >
                Save
              </button>
            ) : null}
            {can('flow:manage') && !viewing && flow.data?.status !== 'active' && !gated ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={publish.isPending}
                onClick={() => publish.mutate()}
              >
                Save & publish
              </button>
            ) : null}
            {can('flow:manage') && !viewing && flow.data?.status !== 'active' && gated ? (
              <button
                type="button"
                className="btn btn-primary"
                disabled={publishOnly.isPending || dirty}
                onClick={() => publishOnly.mutate()}
              >
                Publish
              </button>
            ) : null}
            {can('flow:manage') && flow.data?.status === 'active' ? (
              <button type="button" className="btn" onClick={() => pause.mutate()}>
                Pause
              </button>
            ) : null}
          </>
        }
      />
      {gated ? (
        <div className="alert alert-info" data-testid="flows-gated">
          Editing flows is part of the Legend plan and above; upgrade in Settings to save changes.
          Publish sends the last saved version of this template flow live.
        </div>
      ) : null}
      {readOnly ? (
        // UI-41 (revised, revised 2): the dead editor says why, whatever the plan
        <div className="alert alert-info" data-testid="flows-read-only-notice">
          Read-only view: editing this flow needs the flow:manage permission; ask an owner or
          manager to change it.
        </div>
      ) : null}
      <ErrorAlert error={err} />
      {err?.code === 'flow_in_use' ? (
        <p>
          {parentFlowOf(err) ? (
            <Link to={`/app/flows/${parentFlowOf(err)}`}>Open the flow that triggers this one</Link>
          ) : (
            <Link to={`/app/automations?flow=${id}`}>Automations that use this flow</Link>
          )}
        </p>
      ) : null}
      <fieldset
        disabled={readOnly || gated}
        data-testid={readOnly ? 'flow-read-only' : undefined}
        style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      >
        <div className="row" style={{ marginBottom: 10 }}>
          <Field label="Flow name">
            <input
              className="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
          <Field label="Folder">
            <input
              className="input"
              placeholder="none"
              aria-label="flow folder"
              value={folder}
              onChange={(e) => {
                setFolder(e.target.value);
                setDirty(true);
              }}
            />
          </Field>
        </div>
        {viewing ? (
          <div className="alert alert-info" data-testid="flow-viewing">
            Viewing; Edit to change.
          </div>
        ) : null}
        <div className="flow-palette" hidden={viewing}>
          {NODE_TYPES.filter((t) => t !== 'trigger').map((t) => (
            <button type="button" key={t} className="btn btn-sm" onClick={() => addNode(t)}>
              + {t.replace('_', ' ')}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => addPrebuilt(followerOnlyNode())}
          >
            + follower only
          </button>
        </div>
        <div className="grid" style={{ gridTemplateColumns: selectedNode ? '1fr 340px' : '1fr' }}>
          <div
            className="flow-canvas"
            data-testid="flow-canvas"
            data-interactive={String(!(gated || readOnly))}
          >
            <ReactFlow
              {...canvasProps(gated || readOnly)}
              nodes={nodes}
              edges={edges}
              nodeTypes={nodeTypes}
              onNodesChange={(c) => {
                onNodesChange(c);
              }}
              onEdgesChange={(c) => {
                onEdgesChange(c);
                setDirty(true);
              }}
              onConnect={onConnect}
              onNodeClick={(_e, n) => setSelected(n.id)}
              onPaneClick={() => setSelected(null)}
              fitView
            >
              <Background />
              <Controls />
              <MiniMap />
            </ReactFlow>
          </div>
          {selectedNode ? (
            <NodeEditor
              node={selectedNode}
              onChange={updateNode}
              onDelete={() => deleteNode(selectedNode.id)}
              flows={(flows.data?.items ?? []).filter((f) => f.id !== id)}
            />
          ) : null}
        </div>
      </fieldset>
    </>
  );
}

export function FlowsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { can, me } = useSession();
  // UI-38 (revised 2): a plan without the flows feature builds from templates only
  const templatesOnly = me?.features.flows === false;
  const [folder, setFolder] = useState<string>('');
  const automations = useAutomations();
  const flows = useQuery({
    queryKey: ['flows', folder],
    queryFn: () =>
      unwrap(api.get<Items<Flow>>(`/portal/flows${qs({ folder: folder || undefined })}`)),
  });
  const all = useQuery({
    queryKey: ['flows'],
    queryFn: () => unwrap(api.get<Items<Flow>>('/portal/flows')),
  });
  // FLW-26: the manager sees hidden built-ins too (disabled, with an Enable button)
  const templates = useQuery({
    queryKey: ['flow-templates', 'all'],
    queryFn: () => unwrap(api.get<Items<FlowTemplate>>('/portal/flows/templates?includeHidden=1')),
  });
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', folder: '', templateId: '', prompt: '' });
  const firstTemplate = templates.data?.items.find((t) => t.enabled)?.id ?? '';
  useEffect(() => {
    if (templatesOnly && form.templateId === '' && firstTemplate !== '')
      setForm((f) => ({ ...f, templateId: firstTemplate }));
  }, [templatesOnly, firstTemplate, form.templateId]);
  const folders = useMemo(
    () => [
      ...new Set(
        (all.data?.items ?? []).map((f) => f.folder).filter((f): f is string => Boolean(f)),
      ),
    ],
    [all.data],
  );
  const create = useMutation({
    mutationFn: async () => {
      const fromPrompt = form.templateId === '' && form.prompt.trim() !== '';
      const draft = fromPrompt
        ? await unwrap(api.post<FlowDraft>('/portal/flows/draft', { prompt: form.prompt }))
        : null;
      const def =
        draft?.definition ??
        (form.templateId === ''
          ? { nodes: [{ id: 'trigger', type: 'trigger', on: 'comment', next: null }] }
          : undefined);
      const flow = await unwrap(
        api.post<Flow>('/portal/flows', {
          name: form.name,
          folder: form.folder || null,
          ...(form.templateId ? { templateId: form.templateId } : { definition: def }),
        }),
      );
      return { flow, source: draft?.source ?? null };
    },
    onSuccess: async ({ flow, source }) => {
      await qc.invalidateQueries({ queryKey: ['flows'] });
      setCreating(false);
      if (source === 'fallback') toast('Drafted without AI (no provider configured)');
      navigate(`/app/flows/${flow.id}`);
    },
  });
  const [saveAs, setSaveAs] = useState<{ flowId: string; name: string; category: string } | null>(
    null,
  );
  const saveTemplate = useMutation({
    mutationFn: (v: { flowId: string; name: string; category: string }) =>
      unwrap(
        api.post<FlowTemplate>(`/portal/flows/${v.flowId}/save-as-template`, {
          name: v.name,
          category: v.category,
        }),
      ),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['flow-templates'] });
      setSaveAs(null);
      toast('Saved as a workspace template');
    },
  });
  const toggleTemplate = useMutation({
    mutationFn: (v: { id: string; enabled: boolean }) =>
      unwrap(api.patch(`/portal/flows/templates/${v.id}`, { enabled: v.enabled })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow-templates'] }),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const deleteTemplate = useMutation({
    mutationFn: (id: string) => unwrap(api.del(`/portal/flows/templates/${id}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flow-templates'] }),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  const useAction = (verb: string) =>
    useMutation({
      mutationFn: (fid: string) => unwrap(api.post(`/portal/flows/${fid}/${verb}`)),
      onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
      onError: (e: ApiError) => toast(e.message, 'error'),
    });
  const duplicate = useAction('duplicate');
  const del = useMutation({
    mutationFn: (fid: string) => unwrap(api.del(`/portal/flows/${fid}`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  // UI-38 (revised): a draft (a template on a Basic plan included) publishes from the list
  const publishRow = useMutation({
    mutationFn: (fid: string) => unwrap(api.post<Flow>(`/portal/flows/${fid}/publish`)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['flows'] }),
    onError: (e: ApiError) => toast(e.message, 'error'),
  });
  return (
    <>
      <PageHeader
        title="Flows"
        subtitle="Reusable DM conversation flows: collect leads, qualify, deliver links, hand off"
        actions={
          can('flow:manage') ? (
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              New flow
            </button>
          ) : null
        }
      />
      <ErrorAlert error={flows.error} />
      <div className="tabs">
        <button
          type="button"
          className={folder === '' ? 'active' : ''}
          onClick={() => setFolder('')}
        >
          All
        </button>
        {folders.map((f) => (
          <button
            type="button"
            key={f}
            className={folder === f ? 'active' : ''}
            onClick={() => setFolder(f)}
          >
            {f}
          </button>
        ))}
      </div>
      <DataTable>
        <table className="table">
          <thead>
            <tr>
              <th>Flow</th>
              <th>Trigger</th>
              <th>Folder</th>
              <th>Linked automations</th>
              <th>Linked posts / stories</th>
              <th>Steps</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(flows.data?.items ?? []).map((f) => {
              const trigger = f.definition.nodes.find((n) => n.type === 'trigger');
              return (
                <tr key={f.id}>
                  <td>
                    <Link to={`/app/flows/${f.id}`}>{f.name}</Link>
                  </td>
                  <td>{trigger?.type === 'trigger' ? trigger.on.replace('_', ' ') : '—'}</td>
                  <td>{f.folder ?? '—'}</td>
                  <td>
                    {(automations.data?.items ?? []).filter((a) => a.flowId === f.id).length > 0 ? (
                      <Link to={`/app/automations?flow=${f.id}`}>
                        {(automations.data?.items ?? []).filter((a) => a.flowId === f.id).length}
                      </Link>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td>
                    {(() => {
                      const bound = (automations.data?.items ?? []).filter(
                        (a) => a.flowId === f.id,
                      );
                      const ids = new Set(
                        bound.flatMap((a) =>
                          a.mediaBinding.kind === 'media' ? a.mediaBinding.mediaIds : [],
                        ),
                      );
                      return bound.some((a) => a.mediaBinding.kind === 'any')
                        ? 'all media'
                        : ids.size;
                    })()}
                  </td>
                  <td>{f.definition.nodes.length}</td>
                  <td>
                    <Badge value={f.status} />
                  </td>
                  <td className="actions">
                    <Link className="btn btn-sm" to={`/app/flows/${f.id}?view=1`}>
                      View
                    </Link>{' '}
                    {can('flow:manage') ? (
                      <Link className="btn btn-sm" to={`/app/flows/${f.id}`}>
                        Edit
                      </Link>
                    ) : null}{' '}
                    <Link className="btn btn-sm" to={`/app/contacts?flow=${f.id}`}>
                      View contacts
                    </Link>{' '}
                    {can('flow:manage') && f.status === 'draft' ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => publishRow.mutate(f.id)}
                      >
                        Publish
                      </button>
                    ) : null}{' '}
                    {can('flow:manage') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => duplicate.mutate(f.id)}
                      >
                        Duplicate
                      </button>
                    ) : null}{' '}
                    {can('flow:manage') ? (
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setSaveAs({ flowId: f.id, name: f.name, category: 'leads' })}
                      >
                        Save as template
                      </button>
                    ) : null}{' '}
                    {can('flow:manage') ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => del.mutate(f.id)}
                      >
                        Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
            {flows.data?.items.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <Empty>No flows yet. Start from a template or from scratch.</Empty>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </DataTable>
      {creating ? (
        <Modal
          title="New flow"
          onClose={() => setCreating(false)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={create.isPending || !form.name}
                onClick={() => create.mutate()}
              >
                Create
              </button>
            </>
          }
        >
          <ErrorAlert error={create.error as ApiError | null} />
          <div className="row">
            <Field label="Flow name">
              <input
                className="input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Folder (optional)">
              <input
                className="input"
                list="folders"
                value={form.folder}
                onChange={(e) => setForm({ ...form, folder: e.target.value })}
              />
              <datalist id="folders">
                {folders.map((f) => (
                  <option key={f} value={f} />
                ))}
              </datalist>
            </Field>
          </div>
          <Field label="Start from" group>
            {templatesOnly ? (
              <p className="muted small">
                Your plan publishes template flows; building one from scratch needs the Legend plan.
              </p>
            ) : null}
            <div className="grid grid-2">
              <label
                className={`card ${form.templateId === '' ? 'plan-card featured' : ''}`}
                style={{ cursor: 'pointer' }}
              >
                <input
                  type="radio"
                  className="pill-input"
                  name="tpl"
                  checked={form.templateId === ''}
                  onChange={() => setForm({ ...form, templateId: '' })}
                />
                <strong>Scratch</strong>
                <div className="muted small">
                  Empty flow, or describe it and we draft the steps.
                </div>
              </label>
              {(templates.data?.items ?? []).map((t) => (
                <label
                  key={t.id}
                  className={`card ${form.templateId === t.id ? 'plan-card featured' : ''}`}
                  style={{
                    cursor: t.enabled ? 'pointer' : 'not-allowed',
                    opacity: t.enabled ? 1 : 0.6,
                  }}
                  data-testid={`template-${t.source}`}
                >
                  <input
                    type="radio"
                    className="pill-input"
                    name="tpl"
                    disabled={!t.enabled}
                    checked={form.templateId === t.id}
                    onChange={() => setForm({ ...form, templateId: t.id })}
                  />
                  <span className="badge badge-primary">{t.category}</span>{' '}
                  {t.source === 'workspace' ? <span className="badge">workspace</span> : null}{' '}
                  <strong>{t.name}</strong>
                  <div className="muted small">{t.description}</div>
                  {can('flow:manage') ? (
                    <div className="row" style={{ marginTop: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => toggleTemplate.mutate({ id: t.id, enabled: !t.enabled })}
                      >
                        {t.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {t.source === 'workspace' ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteTemplate.mutate(t.id)}
                        >
                          Delete
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </label>
              ))}
            </div>
          </Field>
          {form.templateId === '' ? (
            <Field
              label="Describe the flow (AI draft)"
              help="Example: ask for name and email, tag as lead, then send the pricing link"
            >
              <textarea
                className="input"
                value={form.prompt}
                onChange={(e) => setForm({ ...form, prompt: e.target.value })}
              />
            </Field>
          ) : null}
        </Modal>
      ) : null}
      {saveAs ? (
        <Modal
          title="Save as workspace template"
          onClose={() => setSaveAs(null)}
          footer={
            <>
              <button type="button" className="btn" onClick={() => setSaveAs(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={saveTemplate.isPending || !saveAs.name.trim()}
                onClick={() => saveTemplate.mutate(saveAs)}
              >
                Save template
              </button>
            </>
          }
        >
          <ErrorAlert error={saveTemplate.error as ApiError | null} />
          <Field label="Template name">
            <input
              className="input"
              value={saveAs.name}
              onChange={(e) => setSaveAs({ ...saveAs, name: e.target.value })}
            />
          </Field>
          <Field label="Category">
            <select
              className="input"
              value={saveAs.category}
              onChange={(e) => setSaveAs({ ...saveAs, category: e.target.value })}
            >
              {['leads', 'sales', 'engagement'].map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </Field>
        </Modal>
      ) : null}
    </>
  );
}
